import type { ColDef, ColGroupDef } from "ag-grid-community";

const isGroup = (def: ColDef | ColGroupDef): def is ColGroupDef =>
  "children" in def;

/** field/colId of the static row-label column, which must stay a fixed size. */
const BENEFITS_LABEL_COLUMN_ID = "benefit.label";

/**
 * Returns column defs with every scalable leaf column's width values scaled
 * by `factor` (1 = unchanged). Drives the narrow/medium/wide column-width
 * presets: with `flex: 1` columns, lowering `minWidth` lets a space-constrained
 * grid pin each column to the scaled width. Only the row-label column keeps
 * its fixed size so the grids stay aligned — other pinned columns (e.g. the
 * current/requested carrier column) scale like any other column.
 */
export const scaleColumnDefWidths = (
  defs: (ColDef | ColGroupDef)[],
  factor: number,
): (ColDef | ColGroupDef)[] => {
  if (factor === 1) return defs;
  return defs.map((def) => {
    if (isGroup(def)) {
      return { ...def, children: scaleColumnDefWidths(def.children, factor) };
    }
    if (
      def.colId === BENEFITS_LABEL_COLUMN_ID ||
      def.field === BENEFITS_LABEL_COLUMN_ID
    ) {
      return def;
    }
    const scaled: ColDef = { ...def };
    if (typeof def.minWidth === "number") {
      scaled.minWidth = Math.round(def.minWidth * factor);
    }
    if (typeof def.width === "number") {
      scaled.width = Math.round(def.width * factor);
    }
    if (typeof def.maxWidth === "number") {
      scaled.maxWidth = Math.round(def.maxWidth * factor);
    }
    return scaled;
  });
};
